import React, { useState, useEffect } from 'react';
import { adminAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, Check, CalendarIcon } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const emptyForm = { code: '', discount_type: 'percentage', discount_value: 10, max_uses: 100, valid_from: '', valid_until: '', site_ids: [], activity_ids: [], active: true };

export const PromosTab = ({ promoCodes, sites = [], loadData, t }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [activities, setActivities] = useState([]);

  useEffect(() => {
    axios.get(`${API}/api/activities`).then(r => setActivities(r.data || [])).catch(() => {});
  }, []);

  const openCreate = () => { setEditingId(null); setForm({ ...emptyForm }); setShowForm(true); };

  const openEdit = (promo) => {
    setEditingId(promo.id);
    setForm({
      code: promo.code || '',
      discount_type: promo.discount_type || 'percentage',
      discount_value: promo.discount_value || 0,
      max_uses: promo.max_uses || 100,
      valid_from: promo.valid_from || '',
      valid_until: promo.valid_until || '',
      site_ids: promo.site_ids || [],
      activity_ids: promo.activity_ids || [],
      active: promo.active !== false,
    });
    setShowForm(true);
  };

  const toggleItem = (field, itemId) => {
    setForm(prev => {
      const current = prev[field] || [];
      return { ...prev, [field]: current.includes(itemId) ? current.filter(s => s !== itemId) : [...current, itemId] };
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingId) {
        const { code, ...updateData } = form;
        await adminAPI.updatePromoCode(editingId, updateData);
        toast.success('Code promo modifie');
      } else {
        await adminAPI.createPromoCode(form);
        toast.success('Code promo cree');
      }
      setShowForm(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const doDelete = async (id) => {
    try { await adminAPI.deletePromoCode(id); toast.success('Supprime'); loadData(); }
    catch { toast.error('Erreur'); }
  };

  const getSiteLabel = (siteId) => {
    const site = sites.find(s => s.id === siteId);
    return site ? (site.city || site.name || siteId) : siteId;
  };

  const getActivityLabel = (actId) => {
    const act = activities.find(a => a.id === actId);
    return act ? act.name : actId;
  };

  // Deduplicate activity names for display (some exist in both sites)
  const uniqueActivities = activities.reduce((acc, act) => {
    if (!acc.find(a => a.id === act.id)) acc.push(act);
    return acc;
  }, []);

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '';
    try { return format(parseISO(dateStr), 'dd MMM yyyy', { locale: fr }); }
    catch { return dateStr; }
  };

  const DatePicker = ({ value, onChange, label }) => {
    const [open, setOpen] = useState(false);
    const selected = value ? parseISO(value) : undefined;
    return (
      <div>
        <Label className="text-sm text-slate-300 mb-1 block">{label}</Label>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="w-full justify-start text-left font-normal bg-[#0A0A0A] border-[#2A2A2A] text-white hover:bg-[#111] hover:text-white"
              data-testid={`promo-date-${label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <CalendarIcon className="mr-2 h-4 w-4 text-slate-500" />
              {value ? formatDateDisplay(value) : <span className="text-slate-500">Choisir une date</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-[#1A1A1A] border-[#2A2A2A]" align="start">
            <Calendar
              mode="single"
              selected={selected}
              onSelect={(date) => {
                if (date) onChange(format(date, 'yyyy-MM-dd'));
                setOpen(false);
              }}
              locale={fr}
              className="rounded-md"
            />
          </PopoverContent>
        </Popover>
      </div>
    );
  };

  return (
    <>
      <div className="flex justify-end mb-6">
        <Button onClick={openCreate} className="bg-[#00FF00] text-black hover:bg-[#00CC00] font-bold" data-testid="new-promo-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouveau code
        </Button>
      </div>

      <div className="bg-[#1A1A1A] rounded-2xl border border-[#2A2A2A] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#111] border-b border-[#2A2A2A]">
              <tr>
                {['Code', 'Reduction', 'Sites', 'Activites', 'Utilisations', 'Validite', 'Statut', ''].map(h => (
                  <th key={h} className="text-left p-4 font-semibold text-slate-400">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {promoCodes.map(p => (
                <tr key={p.id} className="border-b border-[#1A1A1A] hover:bg-[#111] transition-colors">
                  <td className="p-4 font-mono font-bold text-[#00FF00]" data-testid={`promo-code-${p.code}`}>{p.code}</td>
                  <td className="p-4 text-white font-semibold">
                    {p.discount_type === 'percentage' ? `${p.discount_value}%` : `${p.discount_value}EUR`}
                  </td>
                  <td className="p-4">
                    {(!p.site_ids || p.site_ids.length === 0) ? (
                      <Badge className="bg-blue-500/15 text-blue-400 border-0 text-[10px]">Tous</Badge>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {p.site_ids.map(sid => (
                          <Badge key={sid} className="bg-[#00FF00]/10 text-[#00FF00] border-0 text-[10px]">{getSiteLabel(sid)}</Badge>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="p-4">
                    {(!p.activity_ids || p.activity_ids.length === 0) ? (
                      <Badge className="bg-blue-500/15 text-blue-400 border-0 text-[10px]">Toutes</Badge>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {p.activity_ids.map(aid => (
                          <Badge key={aid} className="bg-purple-500/10 text-purple-400 border-0 text-[10px]">{getActivityLabel(aid)}</Badge>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="p-4 text-slate-400">{p.current_uses}/{p.max_uses}</td>
                  <td className="p-4 text-slate-500 text-xs">
                    {formatDateDisplay(p.valid_from)} &rarr; {formatDateDisplay(p.valid_until)}
                  </td>
                  <td className="p-4">
                    <Badge className={`border-0 text-[10px] ${p.active !== false ? 'bg-[#00FF00]/15 text-[#00FF00]' : 'bg-red-500/15 text-red-400'}`}>
                      {p.active !== false ? 'Actif' : 'Inactif'}
                    </Badge>
                  </td>
                  <td className="p-4">
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(p)} className="text-blue-400 hover:text-blue-300" data-testid={`edit-promo-${p.code}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setConfirmDelete({ id: p.id, name: p.code })} className="text-red-400 hover:text-red-300" data-testid={`delete-promo-${p.code}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {promoCodes.length === 0 && (
                <tr><td colSpan={8} className="p-8 text-center text-slate-600">Aucun code promo</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create / Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-lg bg-[#1A1A1A] border-[#2A2A2A] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-white">{editingId ? 'Modifier le code promo' : 'Nouveau code promo'}</DialogTitle>
            <DialogDescription className="text-slate-500">{editingId ? `Modifier ${form.code}` : 'Creer un nouveau code'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm text-slate-300">Code</Label>
              <Input
                value={form.code}
                onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
                className="bg-[#0A0A0A] border-[#2A2A2A] text-white font-mono"
                disabled={!!editingId}
                placeholder="EX: SUMMER20"
                data-testid="promo-form-code"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-slate-300">Type</Label>
                <Select value={form.discount_type} onValueChange={v => setForm({ ...form, discount_type: v })}>
                  <SelectTrigger className="bg-[#0A0A0A] border-[#2A2A2A] text-white" data-testid="promo-form-type"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#2A2A2A]">
                    <SelectItem value="percentage">Pourcentage (%)</SelectItem>
                    <SelectItem value="fixed">Montant fixe (EUR)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm text-slate-300">Valeur</Label>
                <Input type="number" value={form.discount_value} onChange={e => setForm({ ...form, discount_value: parseFloat(e.target.value) || 0 })} className="bg-[#0A0A0A] border-[#2A2A2A] text-white" data-testid="promo-form-value" />
              </div>
            </div>

            {/* Date pickers with calendar */}
            <div className="grid grid-cols-2 gap-4">
              <DatePicker label="Du" value={form.valid_from} onChange={v => setForm({ ...form, valid_from: v })} />
              <DatePicker label="Au" value={form.valid_until} onChange={v => setForm({ ...form, valid_until: v })} />
            </div>

            <div>
              <Label className="text-sm text-slate-300">Max utilisations</Label>
              <Input type="number" value={form.max_uses} onChange={e => setForm({ ...form, max_uses: parseInt(e.target.value) || 0 })} className="bg-[#0A0A0A] border-[#2A2A2A] text-white" data-testid="promo-form-max-uses" />
            </div>

            {/* Site selection */}
            <div>
              <Label className="text-sm text-slate-300 mb-2 block">Sites</Label>
              <p className="text-xs text-slate-500 mb-2">Vide = tous les sites</p>
              <div className="flex flex-wrap gap-2">
                {sites.map(site => {
                  const selected = (form.site_ids || []).includes(site.id);
                  return (
                    <button key={site.id} type="button" onClick={() => toggleItem('site_ids', site.id)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border ${selected ? 'bg-[#00FF00]/15 text-[#00FF00] border-[#00FF00]/30' : 'bg-[#0A0A0A] text-slate-500 border-[#2A2A2A] hover:border-slate-500'}`}
                      data-testid={`promo-site-${site.id}`}
                    >
                      {selected && <Check className="w-3 h-3 inline mr-1" />}{site.city || site.name || site.id}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Activity selection */}
            <div>
              <Label className="text-sm text-slate-300 mb-2 block">Activites</Label>
              <p className="text-xs text-slate-500 mb-2">Vide = toutes les activites</p>
              <div className="flex flex-wrap gap-2">
                {uniqueActivities.map(act => {
                  const selected = (form.activity_ids || []).includes(act.id);
                  return (
                    <button key={act.id} type="button" onClick={() => toggleItem('activity_ids', act.id)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border ${selected ? 'bg-purple-500/15 text-purple-400 border-purple-500/30' : 'bg-[#0A0A0A] text-slate-500 border-[#2A2A2A] hover:border-slate-500'}`}
                      data-testid={`promo-activity-${act.id}`}
                    >
                      {selected && <Check className="w-3 h-3 inline mr-1" />}{act.name}
                      <span className="ml-1 text-[10px] opacity-60">({getSiteLabel(act.site_id)})</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Active toggle */}
            {editingId && (
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} className="accent-[#00FF00] w-4 h-4" data-testid="promo-form-active" />
                <span className="text-sm text-slate-300">Code actif</span>
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowForm(false)} className="text-slate-400">Annuler</Button>
            <Button onClick={handleSubmit} className="bg-[#00FF00] text-black hover:bg-[#00CC00] font-bold" data-testid="submit-promo-btn">
              {editingId ? 'Enregistrer' : 'Creer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(open) => { if (!open) setConfirmDelete(null); }}>
        <AlertDialogContent className="bg-[#1A1A1A] border-[#2A2A2A]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white font-heading">{t('admin.delete_confirm_title')}</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              {t('admin.delete_confirm_desc')} <strong className="text-white">{confirmDelete?.name}</strong> ? {t('admin.delete_irreversible')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#2A2A2A] text-slate-300 border-[#333] hover:bg-[#333]">{t('admin.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => { doDelete(confirmDelete.id); setConfirmDelete(null); }} className="bg-red-600 text-white hover:bg-red-700 font-bold">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
